import { spawn } from "node:child_process";
import * as fs from "node:fs";
import * as path from "node:path";

import {
  parseVideoEvidenceTimestamp,
  type PlannedVideoStateEvidenceSample,
  type VideoStateEvidenceManifest,
  type VideoStateEvidencePlan,
} from "../../domain/ingest/video-state-evidence.js";
import type { WorkspaceStore } from "./workspace-store.js";

const FRAME_SCALE = "scale='if(gt(a,1),min(960,iw),-2)':'if(gt(a,1),-2,min(960,ih))'";
const FRAME_EXTRACTION_CONCURRENCY = 4;

export interface FrameExtractionResult {
  ok: boolean;
  error?: string;
}

export type VideoFrameExtractor = (
  sourcePath: string,
  timestamp: string,
  outputPath: string,
  signal?: AbortSignal,
) => Promise<FrameExtractionResult>;

export interface VideoStateEvidenceMaterializeOptions {
  /** Reuse a ready frame already present at the deterministic output path. */
  reuseExistingFrames?: boolean;
}

function ffmpegTimestamp(timestamp: string): string | null {
  const milliseconds = parseVideoEvidenceTimestamp(timestamp);
  return milliseconds === null ? null : (milliseconds / 1000).toFixed(3);
}

export const extractVideoEvidenceFrame: VideoFrameExtractor = async (
  sourcePath,
  timestamp,
  outputPath,
  signal,
) => {
  if (signal?.aborted) throw new Error("aborted");
  const seek = ffmpegTimestamp(timestamp);
  if (seek === null) return { ok: false, error: "invalid timestamp" };
  try {
    if (!fs.existsSync(sourcePath) || fs.statSync(sourcePath).size === 0) {
      return { ok: false, error: "source video is unavailable or empty" };
    }
  } catch {
    return { ok: false, error: "source video is unavailable" };
  }

  fs.mkdirSync(path.dirname(outputPath), { recursive: true });
  const partial = `${outputPath}.part`;
  fs.rmSync(partial, { force: true });
  return new Promise((resolve, reject) => {
    const child = spawn("ffmpeg", [
      "-y", "-loglevel", "error",
      "-ss", seek,
      "-i", sourcePath,
      "-frames:v", "1",
      "-vf", FRAME_SCALE,
      "-q:v", "3",
      "-f", "image2",
      partial,
    ], { stdio: ["ignore", "ignore", "pipe"] });
    child.stderr?.resume();
    const onAbort = (): void => {
      child.kill("SIGKILL");
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    child.on("error", () => {
      signal?.removeEventListener("abort", onAbort);
      fs.rmSync(partial, { force: true });
      if (signal?.aborted) reject(new Error("aborted"));
      else resolve({ ok: false, error: "frame extractor unavailable" });
    });
    child.on("close", (code) => {
      signal?.removeEventListener("abort", onAbort);
      if (signal?.aborted) {
        fs.rmSync(partial, { force: true });
        reject(new Error("aborted"));
        return;
      }
      if (code === 0 && fs.existsSync(partial) && fs.statSync(partial).size > 0) {
        fs.renameSync(partial, outputPath);
        resolve({ ok: true });
        return;
      }
      fs.rmSync(partial, { force: true });
      resolve({ ok: false, error: `frame extraction failed (exit ${code ?? "unknown"})` });
    });
  });
};

function publicSample(sample: PlannedVideoStateEvidenceSample): Omit<PlannedVideoStateEvidenceSample, "source_path"> {
  const { source_path: _sourcePath, ...rest } = sample;
  return rest;
}

/** Materialize a plan without making frame-tooling failures fatal to ingest. */
export async function materializeVideoStateEvidence(
  store: WorkspaceStore,
  plan: VideoStateEvidencePlan,
  signal?: AbortSignal,
  extractor: VideoFrameExtractor = extractVideoEvidenceFrame,
  options: VideoStateEvidenceMaterializeOptions = {},
): Promise<VideoStateEvidenceManifest> {
  const reuseExistingFrames = options.reuseExistingFrames !== false;
  const plannedSamples = [
    ...plan.items.flatMap((item) => item.samples),
    ...(plan.identities ?? []).flatMap((identity) => identity.samples),
  ];
  const materialized = new Map<
    PlannedVideoStateEvidenceSample,
    Omit<PlannedVideoStateEvidenceSample, "source_path">
  >();
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= plannedSamples.length) return;
      if (signal?.aborted) throw new Error("aborted");
      const sample = plannedSamples[index]!;
      const outputPath = store.path(sample.frame_path);
      let result: FrameExtractionResult;
      if (reuseExistingFrames && fs.existsSync(outputPath) && fs.statSync(outputPath).size > 0) {
        result = { ok: true };
      } else {
        // An explicit refresh must not silently keep a frame produced from an
        // older video at the same episode/timestamp path.
        fs.rmSync(outputPath, { force: true });
        result = await extractor(sample.source_path, sample.timestamp, outputPath, signal);
      }
      materialized.set(sample, publicSample({
        ...sample,
        frame_status: result.ok ? "ready" : "unavailable",
        ...(result.error ? { error: result.error } : {}),
      }));
    }
  };
  await Promise.all(Array.from(
    { length: Math.min(FRAME_EXTRACTION_CONCURRENCY, plannedSamples.length) },
    () => worker(),
  ));
  const materializeSamples = (
    samples: readonly PlannedVideoStateEvidenceSample[],
  ): Omit<PlannedVideoStateEvidenceSample, "source_path">[] => samples.map((sample) => materialized.get(sample)!);

  const items = plan.items.map((item) => ({ ...item, samples: materializeSamples(item.samples) }));
  const identities = (plan.identities ?? [])
    .map((identity) => ({ ...identity, samples: materializeSamples(identity.samples) }));
  const manifest: VideoStateEvidenceManifest = {
    version: 2,
    generated_at: new Date().toISOString(),
    source_sha256: plan.source_sha256,
    items,
    ...(identities.length > 0 ? { identities } : {}),
  };
  if (store.has("evidence/state-evidence.json")) {
    try {
      const existing = store.readJson<VideoStateEvidenceManifest>("evidence/state-evidence.json");
      // identities must take part in the comparison, or a plan that only changed
      // identity frames would keep serving the stale sidecar.
      const comparable = (value: VideoStateEvidenceManifest) => JSON.stringify({
        version: value.version,
        source_sha256: value.source_sha256,
        items: value.items,
        identities: value.identities ?? [],
      });
      if (comparable(existing) === comparable(manifest)) return existing;
    } catch { /* replace a malformed or obsolete sidecar */ }
  }
  store.writeJson("evidence/state-evidence.json", manifest);
  return manifest;
}
